import React from "react";
import { Badge } from "../../components/ui/badge";
import ImprovementSection from "./ImprovementSection";
import { ProductQualityMetric } from "../../types/api";
import { ProductScore } from "../../store/score/getProductScore";
import { addPendingChange } from "../../store/flowSlice";
import { useDispatch } from "react-redux";
import { useTranslation } from "../../i18n/hooks";

interface AltTextImprovementsProps {
  product: ProductQualityMetric;
  productScore: ProductScore;
  jobId: string;
  showAllFields: boolean;
}

export const AltTextImprovements = ({
  product,
  productScore,
  jobId,
  showAllFields,
}: AltTextImprovementsProps): React.ReactElement | null => {
  const dispatch = useDispatch();
  const improvement = productScore.imagesAltTextImprovement;
  const { t } = useTranslation();
  
  const hasAnyAltTextChange = product.images_objects?.some(
    (imgObject) =>
      product.old_images_objects.find(
        (oldImg) => oldImg.source_id === imgObject.source_id
      )?.alt_text !== imgObject.alt_text
  );

  if (!hasAnyAltTextChange && !showAllFields) {
    return null;
  }

  return (
    <div className="p-4 rounded-lg border-2 border-gray-100">
      <div className="flex gap-4">
        <h2 className="font-semibold">{t('altTextImprovements.title')}</h2>
        <Badge
          className={`${
            improvement > 0
              ? "bg-green-50 text-green-700 hover:bg-green-50"
              : improvement < 0
                ? "bg-red-50 text-red-700 hover:bg-red-50"
                : "bg-gray-50 text-gray-700 hover:bg-gray-50"
          }`}
        >
          {improvement > 0 ? "+" : ""}
          {improvement}%
        </Badge>
      </div>
      {product.images_objects?.map((imgObject, idx) => (
        <ImprovementSection
          key={idx}
          label={t('altTextImprovements.image_alt_text_number', { number: idx + 1 })}
          oldValue={
            product.old_images_objects.find(
              (oldImg) => oldImg.source_id === imgObject.source_id
            )?.alt_text
          }
          newValue={imgObject?.alt_text}
          imageUrl={imgObject?.image_url}
          improvement={productScore.imagesAltTextImprovement}
          improvements={productScore.imagesAltTextImprovements}
          forceShow={showAllFields}
          fieldName="alt_text"
          productId={product.product_id}
          jobId={jobId}
          sourceId={imgObject.source_id}
          onEdit={async (newValue: string): Promise<void> => {
            dispatch(
              addPendingChange({
                enrichmentJobId: jobId,
                productId: product.product_id,
                fieldName: "alt_text",
                fieldValue: newValue,
                sourceId: imgObject.source_id,
              })
            );
          }}
        />
      ))}
    </div>
  );
};
